// @flow
import * as React from 'react'
import { FormattedMessage } from 'mz-intl'
import pickExcept from 'mz-utils/pickExcept'
import shallowEqual from 'mz-utils/shallowEqual'
import capitalizeText from 'mz-utils/capitalizeText'
import StripeField from '../StripeField'
import TextField from '../../TextField/field'
import CheckboxField from '../../CheckboxField/field'
import RadioField from '../../RadioField'
import FieldController from '../../Field/FieldController'
import useLatestValueRef from 'mz-utils/useLatestValueRef'
import { createField, wrapWithField } from '../../Field'
import { Row } from '../../../core/Row'
import messages from '../messages.intl'
import {
  ASYNC_PAYMENT_METHOD,
  PAY_WITH_INTENT,
  SUPPORTED_PROVIDERS,
  CARD_BRAND_MAP,
} from '../constants'
import {
  Container,
  HeaderLabel,
  ExtraFieldsContainer,
  WarningMessage,
  CardIcons,
  CardIconWrapper,
  HeaderText,
} from './elements'

// Types
import type { Input, Meta } from '../../../core/BaseField'
import type { NestFunc } from '../../Field'
import type { MethodProps } from '..'
import type { StripePaymentIntent } from '../stripe'

export type Props = MethodProps & {
  input: Input,
  meta: Meta,
  nest: NestFunc,
  controller: FieldController,
}

export type StripeIntentGetter = () => Promise<{
  type: typeof PAY_WITH_INTENT,
  intent: StripePaymentIntent,
  origin: string,
}>

// Utils
const activeFieldComparator = (val, activeVal) =>
  shallowEqual(val, activeVal, {
    include: { type: true, getOption: true },
  })

/**
 * Just a set of user related fields
 * @type {React.Component}
 */
const UserDetailsField = createField((props, nest, { disabled }) => (
  <Row>
    <TextField
      {...nest('firstName')}
      label={<FormattedMessage message={messages.FIRST_NAME} />}
      placeholder={<FormattedMessage message={messages.FIRST_NAME} />}
      disabled={disabled}
      name="fname"
      autoComplete="given-name"
      normalize={capitalizeText}
      required
    />
    <TextField
      {...nest('lastName')}
      label={<FormattedMessage message={messages.LAST_NAME} />}
      placeholder={<FormattedMessage message={messages.LAST_NAME} />}
      disabled={disabled}
      name="lname"
      autoComplete="family-name"
      normalize={capitalizeText}
      required
    />
  </Row>
))

const RadioLabel = () => (
  <HeaderLabel>
    <HeaderText>
      <FormattedMessage message={messages.ADD_NEW_CREDIT_CARD} />
    </HeaderText>
    <CardIcons>
      {Object.keys(SUPPORTED_PROVIDERS).map((k) => {
        const CardIcon = SUPPORTED_PROVIDERS[k]
        return (
          <CardIconWrapper key={k}>
            <CardIcon />
          </CardIconWrapper>
        )
      })}
    </CardIcons>
  </HeaderLabel>
)

function CreditCardForm(props: any) {
  const {
    nest,
    meta: { warning },
    disabled,
    renderExtraCardFields,
    canSaveCreditCard,
    hideCvcField,
    onCreditCardChange,
    elements,
  } = props

  const extraFields =
    !!renderExtraCardFields && renderExtraCardFields(props, nest)

  return (
    <div>
      <UserDetailsField {...nest('userDetails')} disabled={disabled} />
      <Row wrap="xs">
        <StripeField
          {...nest('cardNumber')}
          input={{ onChange: onCreditCardChange }}
          label={<FormattedMessage message={messages.CARD_NUMBER} />}
          elements={elements}
          disabled={disabled}
          required
        />
        <Row>
          <StripeField
            {...nest('cardExpiry')}
            label={<FormattedMessage message={messages.CARD_EXPIRY} />}
            elements={elements}
            disabled={disabled}
            required
          />
          {!hideCvcField && (
            <StripeField
              {...nest('cardCvc')}
              label={<FormattedMessage message={messages.CARD_CVC} />}
              elements={elements}
              disabled={disabled}
              required
            />
          )}
        </Row>
      </Row>
      <div>
        {!!canSaveCreditCard && (
          <ExtraFieldsContainer>
            <CheckboxField
              {...nest('save')}
              label={<FormattedMessage message={messages.SAVE_CERDIT_CARD} />}
              disabled={disabled || !!warning}
            />
            {warning && (
              <WarningMessage>
                <FormattedMessage message={warning} />
              </WarningMessage>
            )}
          </ExtraFieldsContainer>
        )}
        {!!extraFields && (
          <ExtraFieldsContainer>{extraFields}</ExtraFieldsContainer>
        )}
      </div>
    </div>
  )
}

/**
 * CreditCardMethod is a Field component that will provide
 * Credit card form values and getStripeSource function which
 * returns a Promise that resolves with the stripe Source
 */
export function NewCreditCardMethod(props: Props) {
  const getOptionRef = useLatestValueRef(authorizePaymentIntent)
  const getOption = React.useCallback(() => getOptionRef.current(), [])
  const elements = React.useMemo(() => props.stripe.elements(), [props.stripe])

  async function authorizePaymentIntent() {
    const normValue = props.controller.value || {}
    const paymentIntent = await props.paymentIntentActions.authorizePaymentIntent(
      {
        userDetails: normValue.userDetails,
        elements: {
          cardNumber: normValue.cardNumber?.element,
          cardExpiry: normValue.cardExpiry?.element,
          cardCvc: normValue.cardCvc?.element,
        },
      }
    )

    return {
      ...pickExcept(normValue, {
        type: true,
        option: true,
        getOption: true,
        cardNumber: true,
        cardCvc: true,
        cardExpiry: true,
        save: false, // TODO how to handle with intents?
      }),
      type: PAY_WITH_INTENT,
      intent: paymentIntent,
      origin: props.config.origin,
      currency: props.currency,
    }
  }

  function handleCreditCardChange(val: *) {
    if (!val) return
    const {
      input: { onChange },
      controller,
    } = props

    const newValue = {
      ...controller.value,
      brand: CARD_BRAND_MAP[val.event.brand],
    }

    if (props.paymentIntent?.authorizedIntent) {
      props.paymentIntentActions.resetIntent()
    }

    onChange && onChange(newValue)
  }

  const { input, meta } = props
  const activeBrand = input.value?.cardNumber?.event?.brand || 'unknown'
  const activeValue = {
    type: ASYNC_PAYMENT_METHOD,
    brand: CARD_BRAND_MAP[activeBrand],
    getOption,
  }

  const active = !!input && activeFieldComparator(input.value, activeValue)

  const handleContainerClick = () => {
    const { onChange } = props.input
    if (active) {
      return
    }
    onChange && onChange(activeValue)
  }

  return (
    <Container $active={active} onClick={handleContainerClick}>
      <RadioField
        input={input}
        meta={meta}
        label={<RadioLabel />}
        activeValueComparator={activeFieldComparator}
        activeValue={activeValue}
        inactiveValue={null}
      />
      {active && (
        <CreditCardForm
          {...props}
          elements={elements}
          onCreditCardChange={handleCreditCardChange}
        />
      )}
    </Container>
  )
}

export default wrapWithField(NewCreditCardMethod)
